import type { TooltipItem } from 'chart.js';

import { formatWallClockHourLocalized } from '@/utils/date';

import type { ChartTheme } from '../theme';

import { formatAxisValue, resolveValueDecimals } from './axisFormat';
import type { MetricAxisSpec } from './axisTypes';
import { inProgressTooltipLines } from './inProgressBucket';
import type { MetricTooltipOptions } from './optionTypes';
import type { MetricSeriesSpec } from './seriesTypes';
import type { MetricChartKind, MetricChartSpec } from './types';
import { formatWeekRange } from './xAxisScale';

type Item = TooltipItem<MetricChartKind>;

/** The axis a series is plotted on. */
function seriesAxis(
    spec: MetricChartSpec<MetricChartKind>,
    seriesSpec: MetricSeriesSpec | undefined
): MetricAxisSpec | undefined {
    if (seriesSpec?.axis === 'right') return spec.right;
    if (seriesSpec?.axis && seriesSpec.axis !== 'left') return spec.extraAxes?.[seriesSpec.axis];
    return spec.left;
}

function titleCallback(spec: MetricChartSpec<MetricChartKind>) {
    const { xAxisKind = 'day', locale } = spec;
    // Overridden for weekly buckets, where the axis shows a week number and the dates it
    // stands for have to live somewhere, and for a spec that names its own title.
    if (spec.tooltipTitle || (xAxisKind === 'category' && spec.categoryLabel)) {
        return {
            title: (items: Item[]): string => {
                const key = items[0]?.label;
                if (key === undefined) return '';
                const title = spec.tooltipTitle ?? spec.categoryLabel;
                return title ? title(key) : key;
            },
        };
    }
    if (xAxisKind === 'hour') {
        return {
            title: (items: Item[]): string =>
                items.length > 0 ? formatWallClockHourLocalized(items[0].label, locale) : '',
        };
    }
    if (xAxisKind === 'week') {
        return {
            title: (items: Item[]): string =>
                items.length > 0 ? formatWeekRange(items[0].label, locale) : '',
        };
    }
    return {};
}

/** Which tooltip items are listed: none at 0 on a stacked chart, none empty if asked. */
function itemFilter(spec: MetricChartSpec<MetricChartKind>, stacked: boolean) {
    if (!stacked && !spec.tooltipSkipEmpty) return {};
    return {
        filter: (item: Item): boolean => {
            const value = item.parsed.y;
            if (value === null || value === undefined) return !stacked && !spec.tooltipSkipEmpty;
            return !(stacked && value === 0);
        },
    };
}

/** The tooltip of a metric chart: themed, one line per series in its axis' format. */
export function buildTooltipOptions(
    spec: MetricChartSpec<MetricChartKind>,
    theme: ChartTheme,
    stacked: boolean
): MetricTooltipOptions {
    const { locale, tooltipExtraLines, tooltipFooterLines } = spec;
    return {
        enabled: spec.tooltip !== false,
        backgroundColor: theme.tooltip.bg,
        titleColor: theme.tooltip.text,
        bodyColor: theme.tooltip.text,
        footerColor: theme.tooltip.text,
        borderColor: theme.tooltip.border,
        borderWidth: 1,
        ...(spec.showTooltipColors === false ? { displayColors: false } : {}),
        ...(spec.tooltipBoxPadding === undefined ? {} : { boxPadding: spec.tooltipBoxPadding }),
        ...itemFilter(spec, stacked),
        callbacks: {
            ...titleCallback(spec),
            label: (item: Item): string => {
                // Datasets are built in `spec.series` order, so the dataset index maps
                // straight back to its series.
                const seriesSpec = spec.series[item.datasetIndex];
                const axis = seriesAxis(spec, seriesSpec);
                const rawValue = item.parsed.y;

                if (rawValue === null || rawValue === undefined) {
                    return `${item.dataset.label ?? ''}: -`;
                }

                const formatted = formatAxisValue(
                    rawValue,
                    axis,
                    locale,
                    resolveValueDecimals(axis, seriesSpec?.decimals),
                    'tooltip'
                );

                return `${item.dataset.label ?? ''}: ${formatted}`;
            },
            // Keyed on the hovered category rather than on a dataset, so a chart can state
            // something about the x position itself.
            ...(tooltipExtraLines || spec.inProgress
                ? {
                      afterBody: (items: Item[]): string[] =>
                          items.length > 0
                              ? [
                                    ...(tooltipExtraLines?.(items[0].dataIndex) ?? []),
                                    ...inProgressTooltipLines(spec, items[0].dataIndex),
                                ]
                              : [],
                  }
                : {}),
            ...(tooltipFooterLines
                ? {
                      footer: (items: Item[]): string[] =>
                          items.length > 0
                              ? tooltipFooterLines(items[0].dataIndex, spec.series)
                              : [],
                  }
                : {}),
        },
    };
}
